export type Point = { x: number; y: number };
export type Rect = { left: number; right: number; top: number; bottom: number };

export type RoutedEdge = {
  key: string;
  points: Point[];
};

export type EdgeJump = {
  key: string;
  point: Point;
  orientation: "horizontal" | "vertical";
};

export function routeOrthogonal(start: Point, end: Point, obstacles: Rect[]) {
  const clearance = 16;
  const clear = (a: Point, b: Point) => {
    if (a.x !== b.x && a.y !== b.y) return false;
    return !obstacles.some((rect) => {
      const containsEndpoint =
        (start.x >= rect.left && start.x <= rect.right && start.y >= rect.top && start.y <= rect.bottom) ||
        (end.x >= rect.left && end.x <= rect.right && end.y >= rect.top && end.y <= rect.bottom);
      if (containsEndpoint) return false;
      if (a.y === b.y) return a.y > rect.top && a.y < rect.bottom && Math.max(Math.min(a.x, b.x), rect.left) < Math.min(Math.max(a.x, b.x), rect.right);
      return a.x > rect.left && a.x < rect.right && Math.max(Math.min(a.y, b.y), rect.top) < Math.min(Math.max(a.y, b.y), rect.bottom);
    });
  };
  const candidates = [
    ...[(start.x + end.x) / 2, ...obstacles.flatMap((rect) => [rect.left - clearance, rect.right + clearance])].map((x) => [start, { x, y: start.y }, { x, y: end.y }, end]),
    ...[start.y, end.y, ...obstacles.flatMap((rect) => [rect.top - clearance, rect.bottom + clearance])].map((y) => [start, { x: start.x, y }, { x: end.x, y }, end]),
  ].filter((route) => route.every((point, index) => index === 0 || clear(route[index - 1], point)));
  const cost = (points: Point[]) => points.reduce((total, point, index) => total + (index ? Math.abs(point.x - points[index - 1].x) + Math.abs(point.y - points[index - 1].y) : 0), 0) + points.length * 80;
  return (candidates.sort((a, b) => cost(a) - cost(b))[0] ?? [start, end]).filter((point, index, route) => index === 0 || point.x !== route[index - 1].x || point.y !== route[index - 1].y);
}

export function pathFromPoints(points: Point[]) {
  return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
}

export function edgeJumps(routes: RoutedEdge[]): EdgeJump[] {
  const jumps: EdgeJump[] = [];
  const between = (value: number, start: number, end: number) => value > Math.min(start, end) + 12 && value < Math.max(start, end) - 12;
  const isEndpoint = (route: RoutedEdge, point: Point) => {
    const first = route.points[0];
    const last = route.points.at(-1);
    return (first.x === point.x && first.y === point.y) || (last?.x === point.x && last.y === point.y);
  };
  routes.forEach((upper, upperIndex) => {
    for (let lowerIndex = 0; lowerIndex < upperIndex; lowerIndex += 1) {
      const lower = routes[lowerIndex];
      for (let index = 1; index < upper.points.length; index += 1) {
        const a = upper.points[index - 1];
        const b = upper.points[index];
        const horizontal = a.y === b.y;
        for (let otherIndex = 1; otherIndex < lower.points.length; otherIndex += 1) {
          const c = lower.points[otherIndex - 1];
          const d = lower.points[otherIndex];
          const otherHorizontal = c.y === d.y;
          if (horizontal === otherHorizontal) continue;
          const point = horizontal ? { x: c.x, y: a.y } : { x: a.x, y: c.y };
          const onUpper = horizontal ? between(point.x, a.x, b.x) : between(point.y, a.y, b.y);
          const onLower = otherHorizontal ? between(point.x, c.x, d.x) : between(point.y, c.y, d.y);
          if (onUpper && onLower) {
            jumps.push({ key: upper.key, point, orientation: horizontal ? "horizontal" : "vertical" });
          } else if (onUpper && isEndpoint(lower, point)) {
            jumps.push({ key: upper.key, point, orientation: horizontal ? "horizontal" : "vertical" });
          } else if (onLower && isEndpoint(upper, point)) {
            jumps.push({ key: lower.key, point, orientation: otherHorizontal ? "horizontal" : "vertical" });
          }
        }
      }
    }
  });
  return jumps;
}

export function jumpPath({ point, orientation }: Pick<EdgeJump, "point" | "orientation">) {
  const radius = 7;
  return orientation === "horizontal"
    ? `M ${point.x - radius} ${point.y} A ${radius} ${radius} 0 0 1 ${point.x + radius} ${point.y}`
    : `M ${point.x} ${point.y - radius} A ${radius} ${radius} 0 0 0 ${point.x} ${point.y + radius}`;
}
